<script lang="ts">
  import { onDestroy } from 'svelte';
  import { get } from 'svelte/store';
  import MapComponent from '../components/Map.svelte';
  import { currentUser } from '../lib/stores/auth';
  import AccountMenu from '../components/AccountMenu.svelte';
  import FreshnessChip from '../components/FreshnessChip.svelte';
  import VoterDetail from '../components/VoterDetail.svelte';
  import VoterPicker from '../components/VoterPicker.svelte';
  import FilterDrawer from '../components/FilterDrawer.svelte';
  import List from './List.svelte';
  import { push, querystring } from 'svelte-spa-router';
  import { filters, activeCount } from '../lib/filters';
  import { getVoter, listVoters } from '../lib/api';
  import { voterIdFromQuery } from '../lib/deepLink';
  import {
    mapPointsStore,
    fetchMapPoints,
    type VoterSummary,
    type VoterMapPoint,
  } from '../lib/stores/voters';
  import { polygonStore, clearPolygon } from '../lib/stores/polygon';
  import {
    OUTREACH_LABELS,
    OUTREACH_PRECEDENCE,
    outreachColor,
    type TextOutreach,
  } from '../lib/outreach';

  let viewMode: 'support' | 'coverage' | 'uncontacted' | 'outreach' = 'support';
  // Precedence order first, "No text data" last.
  const OUTREACH_LEGEND: (TextOutreach | 'none')[] = [...OUTREACH_PRECEDENCE, 'none'];
  let audience: 'targeted' | 'all' = 'targeted';
  // Skip the first reactive firing — Map.svelte's onMount already fetches
  // map points with the default audience. Without this guard we'd re-fetch
  // immediately at startup and double-paint markers.
  let skipFirstAudienceFetch = true;
  let selectedVoter: VoterSummary | null = null;
  let voterGroup: VoterMapPoint[] | null = null;
  let mapComponent: MapComponent;
  let showFilter = false;
  let showList = false;
  let drawing = false;
  $: polygonMode = drawing ? 'drawing' : ($polygonStore ? 'active' : 'idle');
  // Polygon committed → drawing automatically false (also handles ESC + cancel paths
  // because the controller tears down on those without setting the store).
  $: if ($polygonStore) drawing = false;

  function onPolygonFabClick() {
    if (polygonMode === 'idle') {
      drawing = true;          // enter draw mode
    } else if (polygonMode === 'drawing') {
      drawing = false;         // cancel; controller tears down on prop flip
    } else {
      drawing = false;
      clearPolygon();          // active → idle via the derived reactive
    }
  }
  // True when a popup is open over the map — used to raise the bottom-right
  // FABs above the sheet so the filter and list toggles aren't hidden under it.
  $: popupOpen = selectedVoter !== null || voterGroup !== null;

  // Page through ALL matches for the current filter set. We can't cap at 500
  // because a single-county filter can match 14k+ voters.
  async function loadVoters() {
    const PAGE = 500;
    let offset = 0;
    const all: VoterSummary[] = [];
    // A newer load, a filter reset, an audience switch or leaving the page
    // bumps loadSeq, so an older response that lands late is dropped.
    const seq = ++loadSeq;
    const key = drawKey();
    try {
      while (true) {
        const resp = await listVoters(get(filters), PAGE, offset, audience);
        if (seq !== loadSeq) return;
        all.push(...resp.items);
        if (all.length >= resp.total || resp.items.length === 0) break;
        offset += PAGE;
      }
      mapComponent?.refreshMarkers(all, false);
      drawnKey = key;
    } catch (e) {
      console.error('Failed to load voters', e);
    }
  }

  // Draw what the filters ask for: the filtered list when any filter is set,
  // otherwise the stored map points, which is what the unfiltered map shows
  // (do-not-contact voters included, which the default list request
  // excludes). No download here: a late map response could otherwise land
  // after sign-out has cleared the stored points.
  async function applyFilters() {
    if (get(activeCount) > 0) return loadVoters();
    const seq = ++loadSeq;
    // An audience switch still fetching fills the store first; drawing the
    // old audience's points now would also cancel the switch's own draw.
    await audienceLoad;
    if (destroyed || seq !== loadSeq) return;
    mapComponent?.refreshMarkers(get(mapPointsStore) as unknown as VoterSummary[], false);
    drawnKey = drawKey();
  }

  // What the map was last fully drawn for (filters and audience). Apply only
  // redraws when that differs from the current choice, which covers a load
  // that failed or never finished, without downloading a finished one again.
  let drawnKey = '';
  function drawKey(): string {
    return JSON.stringify([get(filters), audience]);
  }

  // Filter and audience changes wait for the Map component's first load
  // ('ready'). Before it, the map paints the whole store and onMapReady then
  // applies whatever was chosen in the meantime, so the two never race. The
  // filter store outlives the page, so filters can already be set on arrival.
  let mapReady = false;
  let loadSeq = 0;
  // The map points fetch of the latest audience switch (settled when none).
  let audienceLoad: Promise<void> = Promise.resolve();
  let destroyed = false;
  onDestroy(() => {
    destroyed = true;
    loadSeq++;
  });

  // The drawer writes the store on inputs that leave the value unchanged (an
  // emptied number field, a re-committed search), so only a real change
  // redraws. Apply redraws on purpose, which also retries a failed load.
  let lastFiltersKey = '';
  $: onFiltersChanged($filters);
  function onFiltersChanged(f: unknown) {
    const key = JSON.stringify(f);
    if (key === lastFiltersKey) return;
    lastFiltersKey = key;
    if (mapReady) void applyFilters();
  }

  // Admin-only audience reload. When the toggle flips, refetch the map
  // points with the new audience and ask the Map component to repaint.
  $: onAudienceChanged(audience);
  function onAudienceChanged(a: 'targeted' | 'all') {
    if (skipFirstAudienceFetch) {
      skipFirstAudienceFetch = false;
      return;
    }
    if (mapReady) void reloadAudience(a);
  }

  function onMapReady() {
    mapReady = true;
    if (audience === 'all') void reloadAudience('all');
    else if (get(activeCount) > 0) void loadVoters();
    void openVoterFromQuery(get(querystring));
  }

  /**
   * Open the voter named in `/map?voter=<id>`, the app's only URL that opens a
   * specific voter (the admin dashboards' write-in table links here).
   *
   * Exported because on:ready does not fire in this project's jsdom test
   * environment, the same reason AdminDashboards.svelte exports load(). Reads
   * the voter through the API rather than the loaded markers: a deep link can
   * name a voter outside the current filters or audience, and waiting for the
   * marker set would then open nothing.
   */
  export async function openVoterFromQuery(qs: string | null): Promise<void> {
    const id = voterIdFromQuery(qs);
    if (!id) return;
    try {
      selectedVoter = await getVoter(id);
    } catch {
      // A stale link — voter removed, confidential, or not visible to this
      // user — leaves the map as it is rather than surfacing an error over it.
    }
  }

  async function reloadAudience(a: 'targeted' | 'all') {
    // A filtered load for the old audience still in flight must not land
    // while the new audience's points load. Then the filters (or none) are
    // applied to the new audience. Targeted and All-HD-51 span the same
    // district extent, so nothing refits the bounds: the markers swap in
    // place under the user's zoom and pan.
    const seq = ++loadSeq;
    audienceLoad = fetchMapPoints({ audience: a }).catch(e => {
      console.error('Failed to load map points for the audience', e);
    });
    await audienceLoad;
    // A redraw started meanwhile (Apply, Reset) waited for this fetch and
    // draws the new audience itself.
    if (destroyed || seq !== loadSeq) return;
    await applyFilters();
  }

  function onSelectVoter(e: CustomEvent<VoterSummary>) {
    selectedVoter = e.detail;
    voterGroup = null;  // close picker if it was open
  }

  function onSelectGroup(e: CustomEvent<VoterMapPoint[]>) {
    voterGroup = e.detail;
  }

  function onPickFromGroup(e: CustomEvent<string>) {
    voterGroup = null;
    mapComponent?.loadVoterById(e.detail);
  }

  function onContactSaved(e: CustomEvent<VoterSummary>) {
    mapComponent?.refreshPin(e.detail);
    selectedVoter = e.detail;
  }

  // A voter edit (for example a DNC toggle) repaints the pin the same way.
  const onVoterUpdated = onContactSaved;

  // A contact or edit made from the List overlay repaints the pin without
  // opening the map's own detail sheet behind the list.
  function onListVoterChanged(e: CustomEvent<VoterSummary>) {
    mapComponent?.refreshPin(e.detail);
  }

  function closeDetail() {
    selectedVoter = null;
  }

  function closeGroup() {
    voterGroup = null;
  }
</script>

<svelte:head><title>Canvass Map</title></svelte:head>

<div class="map-page">
  <div class="top-bar">
    <span class="brand">
      <svg class="brand-mark" viewBox="0 0 36 18" aria-hidden="true">
        <circle cx="27" cy="8" r="3.5" fill="var(--sun-bright)"/>
        <path d="M0 14 Q 9 8 18 11 T 36 12 L 36 18 L 0 18 Z" fill="var(--leaf-bright)" opacity="0.65"/>
        <path d="M0 17 Q 11 11 21 14 T 36 15 L 36 18 L 0 18 Z" fill="var(--leaf)"/>
      </svg>
      <span class="brand-name">Canvass</span>
      <span class="brand-sub">· April Montgomery · NC-51</span>
    </span>
    <FreshnessChip />
    <AccountMenu />
  </div>

  <div class="map-area">
    <MapComponent
      bind:this={mapComponent}
      on:ready={onMapReady}
      {viewMode}
      {polygonMode}
      on:selectVoter={onSelectVoter}
      on:selectGroup={onSelectGroup}
      on:walkThisArea={() => push('/walk-list')}
      on:cancel={() => (drawing = false)}
    />

    {#if !showList && !showFilter}
      <div class="toggle-stack no-print">
        <!-- View mode is available to everyone (Support / Coverage / Uncontacted / Outreach). -->
        <div class="view-toggle" role="group" aria-label="View mode">
          <button class:active={viewMode === 'support'} on:click={() => (viewMode = 'support')}>Support</button>
          <button class:active={viewMode === 'coverage'} on:click={() => (viewMode = 'coverage')}>Coverage</button>
          <button class:active={viewMode === 'uncontacted'} on:click={() => (viewMode = 'uncontacted')}>Uncontacted</button>
          <button class:active={viewMode === 'outreach'} on:click={() => (viewMode = 'outreach')}>Outreach</button>
        </div>
        {#if $currentUser?.role === 'admin'}
          <div class="view-toggle audience-toggle" role="group" aria-label="Audience">
            <button class:active={audience === 'targeted'} on:click={() => (audience = 'targeted')}>Targeted+new</button>
            <button class:active={audience === 'all'} on:click={() => (audience = 'all')}>All HD-51</button>
          </div>
        {/if}
      </div>
    {/if}

    {#if viewMode === 'coverage' && !showList && !showFilter}
      <div class="coverage-legend no-print" aria-label="Coverage legend">
        <span><i class="dot" style="background:#006d2c"></i>&lt;1w</span>
        <span><i class="dot" style="background:#74c476"></i>&lt;1mo</span>
        <span><i class="dot" style="background:#f59e0b"></i>&lt;2mo</span>
        <span><i class="dot" style="background:#f46d43"></i>&lt;4mo</span>
        <span><i class="dot" style="background:#a50026"></i>stale</span>
      </div>
    {/if}

    {#if viewMode === 'uncontacted' && !showList && !showFilter}
      <div class="coverage-legend no-print" aria-label="Uncontacted legend">
        <span class="heat-note">Heat = nobody has ever knocked here</span>
        <span><i class="dot" style="background:#74c476"></i>fewer</span>
        <span><i class="dot" style="background:#f59e0b"></i>some</span>
        <span><i class="dot" style="background:#a50026"></i>many</span>
      </div>
    {/if}

    {#if viewMode === 'outreach' && !showList && !showFilter}
      <div class="coverage-legend outreach-legend no-print" aria-label="Outreach legend">
        {#each OUTREACH_LEGEND as state (state)}
          <span>
            <i
              class="dot"
              style="background:{state === 'none' ? '#ffffff' : outreachColor(state)};box-shadow:inset 0 0 0 2px {outreachColor(state)}"
            ></i>{OUTREACH_LABELS[state]}
          </span>
        {/each}
      </div>
    {/if}

    <!-- Floating controls bottom-right; raised above the sheet when a popup is open -->
    <div class="map-controls" class:raised={popupOpen}>
      <button
        class="fab"
        class:active={showList}
        on:click={() => (showList = !showList)}
        aria-label="Toggle list view"
      >
        ☰
      </button>
      <button
        class="fab"
        class:active={showFilter}
        on:click={() => (showFilter = !showFilter)}
        aria-label="Toggle filters"
      >
        ⚙
        {#if $activeCount > 0}
          <span class="badge">{$activeCount}</span>
        {/if}
      </button>
      <button
        class="fab"
        on:click={() => push('/walk-list')}
        aria-label="Open walk list"
        title="Walk list (printable)"
      >
        📋
      </button>
      <button
        class="fab"
        class:active={polygonMode !== 'idle'}
        on:click={onPolygonFabClick}
        aria-label={polygonMode === 'idle' ? 'Draw walk-area polygon' : polygonMode === 'drawing' ? 'Cancel polygon drawing' : 'Clear polygon'}
        title={polygonMode === 'idle' ? 'Draw walk-area polygon' : polygonMode === 'drawing' ? 'Cancel polygon drawing' : 'Clear polygon'}
      >
        {polygonMode === 'idle' ? '⬡' : polygonMode === 'drawing' ? '✕' : '🗑'}
      </button>
    </div>

    <!-- List overlay (renders above map) -->
    {#if showList}
      <div class="list-overlay">
        <List on:contactSaved={onListVoterChanged} on:voterUpdated={onListVoterChanged} />
      </div>
    {/if}
  </div>

  <!-- Filter drawer (slides in from right, outside map-area so z-index stacks correctly) -->
  <FilterDrawer
    open={showFilter}
    on:apply={() => {
      // Filters already apply as they change (the drawer flushes a pending
      // search before dispatching). Apply redraws only when the map is not
      // drawn for the current choice, which retries a failed load. If the
      // search flush changed the filters, Apply's load starts first and the
      // flush's reactive load supersedes it with the same filters.
      showFilter = false;
      if (mapReady && drawnKey !== drawKey()) void applyFilters();
    }}
    on:close={() => (showFilter = false)}
  />

  {#if voterGroup}
    <VoterPicker
      voters={voterGroup}
      viewMode={viewMode}
      on:pick={onPickFromGroup}
      on:close={closeGroup}
    />
  {/if}

  {#if selectedVoter}
    <VoterDetail
      voter={selectedVoter}
      on:close={closeDetail}
      on:contactSaved={onContactSaved}
      on:voterUpdated={onVoterUpdated}
    />
  {/if}
</div>

<style>
  .map-page {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100dvh;
    overflow: hidden;
  }
  .top-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    background: var(--leaf);
    color: var(--paper);
    border-bottom: 2px solid var(--sun);
    z-index: 10;
    flex-shrink: 0;
    /* Subtle ruled-page texture echoing the login hero */
    background-image:
      radial-gradient(circle at 92% 50%, rgba(229, 178, 64, .12) 0%, transparent 40%);
  }
  .brand {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    min-width: 0;
  }
  .brand-mark {
    width: 36px;
    height: 18px;
    flex-shrink: 0;
    filter: drop-shadow(0 1px 1px rgba(0,0,0,0.25));
  }
  .brand-name {
    font-family: var(--font-display);
    font-size: 1.15rem;
    font-weight: 600;
    color: var(--paper);
    letter-spacing: -0.01em;
  }
  .brand-sub {
    font-family: var(--font-body);
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--sun-bright);
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* Hide the sub-label on narrow viewports — keep the brand-name + mark */
  @media (max-width: 520px) {
    .brand-sub { display: none; }
    .top-bar { padding: 8px 12px; gap: 8px; }
  }
  .map-area {
    position: relative;
    flex: 1;
    overflow: hidden;
  }
  .map-controls {
    position: absolute;
    bottom: 5rem;
    right: 1rem;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: bottom 0.15s ease-out;
  }
  /* When a voter detail or picker is open the bottom sheet covers 50dvh.
     Raise the FABs just above the sheet so they remain reachable. */
  .map-controls.raised {
    bottom: calc(50dvh + 1rem);
  }
  .fab {
    position: relative;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: white;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    font-size: 1.2rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .fab.active {
    background: #1d4ed8;
    color: white;
  }
  .fab .badge {
    position: absolute;
    top: -4px;
    right: -4px;
    background: #ef4444;
    color: white;
    border-radius: 50%;
    width: 16px;
    height: 16px;
    font-size: 0.65rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .list-overlay {
    position: absolute;
    inset: 0;
    background: white;
    z-index: 200;
    overflow: hidden;
  }
  .toggle-stack {
    position: absolute;
    top: 4.5rem;
    left: 1rem;
    z-index: 500;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    /* The stack's own box has no visible fill or border, so its transparent
       area must not intercept map pans/taps that land beside the buttons. */
    pointer-events: none;
    /* Reserve just enough of the right edge to clear the 44px My Location
       button (right: 1rem) at a 360px phone width: the button's left edge
       sits at x=300, so this leaves the stack's own right edge at x=296, a
       4px clearance. A 5.5rem reservation left only 272px for the four
       view-mode buttons, 1px short of the 273px they need, so "Outreach"
       wrapped alone. */
    max-width: calc(100vw - 5rem);
  }
  .view-toggle {
    display: flex;
    flex-wrap: wrap;
    background: var(--paper);
    border: 1px solid var(--rule-strong);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    /* Buttons themselves stay clickable; only the stack's transparent
       padding around them is click-through. */
    pointer-events: auto;
  }
  .view-toggle button {
    padding: 6px 12px;
    border: none;
    background: transparent;
    font-family: var(--font-body);
    font-size: 0.82rem;
    cursor: pointer;
    color: var(--ink);
  }
  .view-toggle button.active {
    background: var(--ink);
    color: var(--paper);
  }
  /* Four view modes do not fit one row at the default size on a small phone.
     Tighten the buttons rather than abbreviate the labels. */
  @media (max-width: 480px) {
    .view-toggle button { padding: 5px 7px; font-size: 0.72rem; }
  }
  .coverage-legend {
    position: absolute;
    bottom: 1rem;
    left: 1rem;
    z-index: 500;
    display: flex;
    gap: 0.75rem;
    align-items: center;
    flex-wrap: wrap;
    row-gap: 0.25rem;
    /* border-box so the max-width reservation below includes the legend's
       own padding, not just its content; otherwise the right edge sits
       flush with the screen at 360-412px widths. */
    box-sizing: border-box;
    max-width: calc(100vw - 2rem);
    padding: 6px 10px;
    background: var(--paper);
    border: 1px solid var(--rule-strong);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    font-size: 0.78rem;
    color: var(--ink);
  }
  .coverage-legend .dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 4px;
    vertical-align: middle;
  }
  .coverage-legend .heat-note {
    font-weight: 600;
    margin-right: 0.25rem;
  }
</style>
